import React from 'react'
import classNames from 'classnames'
import DateFormatter from '@/components/DateFormatter/DateFormatter'
import IconSymplaPlay from '@/icons/social/iconSymplaPlay'
import { SearchEvent } from './searchEvent'
import { DEFAULT_IMAGE, eventType } from '@/constante/event'
import * as styles from './EventCard.css'
import { Button } from '@/components/Button'
import IconReport from '@/icons/communication/iconReport'

type CardLayout = 'default' | 'lateral' | 'lateral-on-mobile' | 'lateral-medium'

type EventCardProps = {
  sectionPosition?: number
  dataCreative?: string
  loadingImage?: 'lazy' | 'eager' | undefined
  layoutCard?: CardLayout
  isHover?: boolean
  fullWidth?: boolean
  ImageComponent?: React.ElementType<React.ImgHTMLAttributes<HTMLImageElement>>
  reportButton?: boolean
  onReport?: (name: string, url: string) => void
  onClick?: React.MouseEventHandler<HTMLAnchorElement>
  tagCard?: string
  tagVariant?: 'default' | 'dark'
  highlight?: boolean
  topEvent?: boolean
  price?: number
} & SearchEvent

function EventCard({
  name,
  location,
  images,
  url,
  duration_type,
  start_date,
  start_date_formats,
  end_date,
  end_date_formats,
  id,
  sectionPosition,
  dataCreative,
  type,
  organizer,
  loadingImage = 'lazy',
  layoutCard = 'default',
  ImageComponent = 'img',
  fullWidth = false,
  isHover = true,
  reportButton = false,
  onReport,
  onClick,
  tagCard,
  tagVariant = 'default',
  highlight = false,
  topEvent = false,
  price,
}: EventCardProps) {
  const getLocationText = () => {
    switch (type) {
      case eventType.online:
        return 'Evento Online'
      case eventType.ondemand:
        return `Por ${organizer?.name || 'Organizador desconhecido'}`
      default:
        return `${location?.name || ''}${location?.city ? ` - ${location.city}` : ''}${location?.state ? `, ${location.state}` : ''}`
    }
  }

  const cardClass = classNames(styles.CardStyle, {
    [styles.CardStyleLateralized]:
      layoutCard === 'lateral' || layoutCard === 'lateral-medium',
    [styles.CardStyleLateralOnMobile]: layoutCard === 'lateral-on-mobile',
    [styles.CardStyleFullWidth]: fullWidth,
  })

  const ContainerImgClass = classNames(styles.ContainerImg, {
    [styles.CardImgLateralized]: layoutCard === 'lateral',
    [styles.CardImgLateralMedium]: layoutCard === 'lateral-medium',
    [styles.CardImgLateralOnMobile]: layoutCard === 'lateral-on-mobile',
    [styles.ContainerImgHightlight]: highlight,
    [styles.CardStyleIsHover]: isHover,
    [styles.CardImgTopEvent]: topEvent,
  })

  const InfoContainerClass = classNames(styles.InfoContainer, {
    [styles.InfoContainerHightlight]: highlight,
    [styles.InfoContainerTopEvent]: topEvent,
  })

  const tagClass = classNames(styles.TagCard, {
    [styles.TagCardDark]: tagVariant === 'dark',
  })

  function handleReportClick(
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    e.preventDefault()

    onReport?.(name, url)
  }

  return (
    <a
      href={url}
      className={`sympla-card ${cardClass}`}
      data-bannerid={`${id}`}
      data-name={`${name}`}
      data-position={`${sectionPosition}`}
      data-creative={`${dataCreative}`}
      id={`${id}`}
      onClick={onClick}
    >
      {topEvent && !!sectionPosition && (
        <div className={styles.TopEvent}>{sectionPosition}</div>
      )}
      <div className={ContainerImgClass}>
        {tagCard && <div className={tagClass}>{tagCard}</div>}
        <ImageComponent
          className={styles.CardImg}
          src={images?.xs || DEFAULT_IMAGE}
          alt={`Imagem do evento ${name}`}
          loading={loadingImage}
        />
      </div>
      <div className={InfoContainerClass}>
        {type === eventType.ondemand && (
          <div
            className={
              layoutCard === 'lateral'
                ? styles.LogoWrapperLateralized
                : styles.LogoWrapper
            }
          >
            <IconSymplaPlay className={styles.LogoWrapperSvg} />
          </div>
        )}
        <h3 className={styles.InfoTitle}>{name}</h3>
        <p className={styles.LocationStyle}>{getLocationText()}</p>
        {type !== eventType.ondemand && (
          <div className={styles.DateStyle}>
            <DateFormatter
              startDate={start_date_formats?.pt || ''}
              endDate={end_date_formats?.pt || ''}
              durationType={duration_type}
              rawStartDate={start_date}
              rawEndDate={end_date}
              variant="neutral"
            />
          </div>
        )}
        {price && (
          <p className={styles.Price}>
            A partir de R$
            {price.toFixed(2).replace('.', ',')}
          </p>
        )}
      </div>
      {reportButton && (
        <Button
          type="button"
          variant="text"
          upperCase={false}
          onClick={handleReportClick}
        >
          <IconReport width={16} height={16} /> Denunciar evento
        </Button>
      )}
    </a>
  )
}

export default EventCard
